import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  Check,
  ChevronDown,
  Code2,
  Download,
  Eye,
  Loader2,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import dark from 'react-syntax-highlighter/dist/esm/styles/prism/atom-dark';
import light from 'react-syntax-highlighter/dist/esm/styles/prism/prism';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useTheme } from 'components/core/theme-provider';
import { MarkdownRenderer } from 'features/chat/agent-chat-module/components/markdown-renderer/markdown-renderer';
import { useConfig } from 'hooks/use-config';
import { useMediaQuery } from 'hooks/use-media-query';
import { useToast } from 'hooks/use-toast';
import { cn, copyTextToClipboard } from 'lib/utils';
import { downloadFileById } from 'services/storage-service';
import { useAssistantFileViewerStore } from 'state/store/chat/use-assistant-file-viewer-store';
import { useAssistantFileContent } from '../hooks/use-assistant-file-content';
import { AssistantCsvTablePreview } from './AssistantCsvTablePreview';
import { AssistantTextPreview } from './AssistantTextPreview';
import {
  getAttachmentDisplayName,
  getAttachmentExtension,
  getAttachmentHighlightLanguage,
  getGoogleDocsViewerUrl,
  getOfficeEmbedUrl,
  isBinaryDocumentAttachment,
  isCsvAttachment,
  isEmbeddableFileUrl,
  isHtmlAttachment,
  isJsonAttachment,
  isMarkdownAttachment,
  isOfficeDocumentAttachment,
  isPdfAttachment,
  isPlainTextAttachment,
  parseDelimitedTable,
  supportsAssistantFileCopy,
  supportsAssistantFileDownload,
  supportsFileViewModeToggle,
} from '../utils/assistant-file-preview';

type SyntaxHighlighterTheme = Record<string, CSSProperties>;

const prismDarkTheme = dark as unknown as SyntaxHighlighterTheme;
const prismLightTheme = light as unknown as SyntaxHighlighterTheme;

type FileViewMode = 'preview' | 'code';

const MIN_PREVIEW_WIDTH = 320;
const MIN_CHAT_WIDTH = 280;

const formatJsonPreview = (content: string) => {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return content;
  }
};

export const AssistantFileViewer = () => {
  const file = useAssistantFileViewerStore((state) => state.file);
  const isExpanded = useAssistantFileViewerStore((state) => state.isExpanded);
  const closeFile = useAssistantFileViewerStore((state) => state.closeFile);
  const toggleExpanded = useAssistantFileViewerStore((state) => state.toggleExpanded);
  const { content, blobUrl, openUrl, isLoading, error, tooLarge } = useAssistantFileContent(file);
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState<FileViewMode>('preview');
  const [copied, setCopied] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const asideRef = useRef<HTMLElement>(null);
  const currentWidthRef = useRef<number | null>(null);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isBinaryDocument = file
    ? isBinaryDocumentAttachment(file.fileName, file.extension)
    : false;
  const showViewModeToggle = file
    ? supportsFileViewModeToggle(file.fileName, file.extension)
    : false;
  const isCsvFile = file ? isCsvAttachment(file.fileName, file.extension) : false;

  useEffect(() => {
    setViewMode('preview');
    setCopied(false);
    setPanelWidth(null);
  }, [file?.fileId]);

  useEffect(() => {
    if (!isResizing) {
      currentWidthRef.current = panelWidth;
    }
  }, [panelWidth, isResizing]);

  useEffect(() => {
    if (!showViewModeToggle && viewMode === 'code') {
      setViewMode('preview');
    }
  }, [showViewModeToggle, viewMode]);

  useEffect(() => {
    if (!file) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeFile();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [file, closeFile]);

  const extension = file
    ? getAttachmentExtension(file.fileName, file.extension)
    : '';
  const displayName = file
    ? getAttachmentDisplayName(file.fileName, file.extension)
    : '';
  const extensionLabel = extension ? extension.toUpperCase() : 'FILE';
  const highlightLanguage = file
    ? getAttachmentHighlightLanguage(file.fileName, file.extension)
    : 'text';
  const isDark = theme === 'dark';
  const canCopy = Boolean(content);
  const showCopyAction = file
    ? supportsAssistantFileCopy(file.fileName, file.extension)
    : false;
  const showDownloadAction = file
    ? supportsAssistantFileDownload(file.fileName, file.extension)
    : false;
  const formattedContent = useMemo(() => {
    if (!file || !content) return content;
    if (isJsonAttachment(file.fileName, file.extension)) {
      return formatJsonPreview(content);
    }
    return content;
  }, [content, file]);

  const csvTable = useMemo(() => {
    if (!file || !content || !isCsvAttachment(file.fileName, file.extension)) {
      return null;
    }
    return parseDelimitedTable(content, getAttachmentExtension(file.fileName, file.extension));
  }, [content, file]);

  if (!file) return null;

  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
    if (asideRef.current) {
      const width = asideRef.current.getBoundingClientRect().width;
      currentWidthRef.current = width;
      asideRef.current.style.width = `${width}px`;
    }
  };

  const handleResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId) || !asideRef.current) return;

    const right = asideRef.current.getBoundingClientRect().right;
    const nextWidth = right - event.clientX;
    const maxWidth = right - MIN_CHAT_WIDTH;

    if (nextWidth >= MIN_PREVIEW_WIDTH && nextWidth <= maxWidth) {
      currentWidthRef.current = nextWidth;
      asideRef.current.style.width = `${nextWidth}px`;
    }
  };

  const handleResizePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsResizing(false);
    if (currentWidthRef.current !== null) {
      setPanelWidth(currentWidthRef.current);
    }
  };

  const renderCodeView = () => {
    if (!formattedContent) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center">
          <p className="text-sm text-muted-foreground">Source is not available for this file.</p>
        </div>
      );
    }

    // CSV/TXT code mode: Claude-style numbered plain source (not prism chrome).
    if (isCsvFile || isPlainTextAttachment(file.fileName, file.extension)) {
      return <AssistantTextPreview content={formattedContent} />;
    }

    return (
      <div className="h-full overflow-auto p-4">
        <SyntaxHighlighter
          showLineNumbers
          language={highlightLanguage}
          style={isDark ? prismDarkTheme : prismLightTheme}
          customStyle={{
            margin: 0,
            padding: '1rem',
            background: 'transparent',
            fontSize: '0.875rem',
            lineHeight: '1.6',
          }}
          PreTag="div"
        >
          {formattedContent}
        </SyntaxHighlighter>
      </div>
    );
  };

  const copyContent = async () => {
    if (!formattedContent) return;
    try {
      await copyTextToClipboard(formattedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Copy failed',
        description: 'Unable to copy this file to the clipboard.',
      });
    }
  };

  const downloadFile = async () => {
    const success = await downloadFileById(
      file.fileId,
      file.fileName,
      configurationSettings,
      undefined
    );
    if (!success) {
      toast({
        variant: 'destructive',
        title: 'Download failed',
        description: 'Unable to download this file. It may no longer be available.',
      });
    }
  };

  const renderPreview = () => {
    if (isPdfAttachment(file.fileName, file.extension)) {
      const embedOpenUrl = openUrl && isEmbeddableFileUrl(openUrl) ? openUrl : '';
      // Prefer local blob with toolbar hidden so Chrome/Edge dark PDF chrome + download control stay gone.
      const pdfSrc = blobUrl
        ? `${blobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`
        : embedOpenUrl
          ? getGoogleDocsViewerUrl(embedOpenUrl)
          : '';

      if (!pdfSrc) {
        return (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm text-muted-foreground">Unable to preview this PDF.</p>
            <Button type="button" variant="outline" size="sm" onClick={downloadFile}>
              Download
            </Button>
          </div>
        );
      }

      return (
        <div className="relative h-full min-h-0 w-full overflow-hidden bg-white dark:bg-background">
          <iframe
            title={file.fileName}
            src={pdfSrc}
            className="absolute inset-0 h-full w-full border-0 bg-white"
          />
        </div>
      );
    }

    if (isOfficeDocumentAttachment(file.fileName, file.extension)) {
      const embedOpenUrl = openUrl && isEmbeddableFileUrl(openUrl) ? openUrl : '';
      if (!embedOpenUrl) {
        return (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm text-muted-foreground">
              Preview is not available for this document. Download it to view the contents.
            </p>
            <Button type="button" variant="outline" size="sm" onClick={downloadFile}>
              Download
            </Button>
          </div>
        );
      }

      const officeSrc = getOfficeEmbedUrl(embedOpenUrl);

      return (
        <div className="relative h-full min-h-0 w-full overflow-hidden bg-muted/10">
          <iframe
            title={file.fileName}
            src={officeSrc}
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
      );
    }

    if (!formattedContent) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-sm text-muted-foreground">
            Preview is not available for this file type.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={downloadFile}>
            Download
          </Button>
        </div>
      );
    }

    if (isHtmlAttachment(file.fileName, file.extension)) {
      return (
        <iframe
          title={file.fileName}
          srcDoc={formattedContent}
          sandbox=""
          className="h-full w-full border-0 bg-background"
        />
      );
    }

    if (isCsvAttachment(file.fileName, file.extension)) {
      if (!csvTable) {
        return (
          <div className="flex h-full items-center justify-center px-6 text-center">
            <p className="text-sm text-muted-foreground">Unable to parse this table.</p>
          </div>
        );
      }
      return <AssistantCsvTablePreview table={csvTable} />;
    }

    if (isPlainTextAttachment(file.fileName, file.extension)) {
      return <AssistantTextPreview content={formattedContent} />;
    }

    if (isMarkdownAttachment(file.fileName, file.extension)) {
      return (
        <div className="mx-auto w-full max-w-3xl px-6 py-6 md:px-11 md:py-8">
          <MarkdownRenderer content={formattedContent} />
        </div>
      );
    }

    return (
      <div className="h-full overflow-auto p-4">
        <SyntaxHighlighter
          showLineNumbers
          language={highlightLanguage}
          style={isDark ? prismDarkTheme : prismLightTheme}
          customStyle={{
            margin: 0,
            padding: '1rem',
            background: 'transparent',
            fontSize: '0.875rem',
            lineHeight: '1.6',
          }}
          PreTag="div"
        >
          {formattedContent}
        </SyntaxHighlighter>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        ref={asideRef}
        className={cn(
          'z-20 flex min-h-0 flex-col overflow-hidden bg-background outline-none',
          isExpanded
            ? 'absolute inset-0 h-full md:inset-2 md:rounded-xl md:border md:border-border md:shadow-sm'
            : cn(
              'absolute inset-0 h-full md:relative md:mr-2 md:h-auto md:max-h-full md:min-w-0 md:shrink-0 md:self-stretch md:rounded-xl md:border md:border-border md:bg-muted/20 md:shadow-sm',
              panelWidth === null && 'md:w-1/2'
            )
        )}
        style={
          isDesktop && !isExpanded && panelWidth !== null
            ? { width: `${panelWidth}px` }
            : undefined
        }
        aria-label={`${displayName} file viewer`}
        tabIndex={-1}
      >
        {!isExpanded && isDesktop ? (
          <div
            className="group absolute left-0 top-0 z-50 hidden h-full w-6 -translate-x-1/2 cursor-ew-resize select-none items-center justify-center outline-none md:flex"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            aria-label="Resize preview panel"
            role="separator"
            aria-orientation="vertical"
          >
            <div
              className={cn(
                'flex h-full w-3 items-center justify-center transition-all group-hover:bg-primary/20',
                isResizing && 'bg-primary/30'
              )}
            >
              <div
                className={cn(
                  'h-8 w-0.5 rounded-full bg-transparent transition-all group-hover:bg-primary',
                  isResizing && 'bg-primary'
                )}
              />
            </div>
          </div>
        ) : null}
        <div className="relative flex min-h-11 shrink-0 items-center justify-between gap-2 px-2 py-2 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:border-b after:border-border after:content-['']">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            {showViewModeToggle ? (
              <div
                role="radiogroup"
                aria-label="File view mode"
                className="relative inline-flex h-8 shrink-0 items-stretch rounded-md bg-muted p-0.5"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={viewMode === 'preview'}
                      aria-label="Preview"
                      className={cn(
                        'inline-flex h-full w-8 items-center justify-center rounded-[5px] text-muted-foreground transition-colors hover:text-foreground',
                        viewMode === 'preview' && 'bg-background text-foreground shadow-sm'
                      )}
                      onClick={() => setViewMode('preview')}
                    >
                      <Eye className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Preview</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={viewMode === 'code'}
                      aria-label="Code"
                      className={cn(
                        'inline-flex h-full w-8 items-center justify-center rounded-[5px] text-muted-foreground transition-colors hover:text-foreground',
                        viewMode === 'code' && 'bg-background text-foreground shadow-sm'
                      )}
                      onClick={() => setViewMode('code')}
                    >
                      <Code2 className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Code</TooltipContent>
                </Tooltip>
              </div>
            ) : null}

            <h2 className="min-w-0 truncate text-sm font-normal text-foreground" title={file.fileName}>
              {displayName}
              <span className="text-muted-foreground opacity-50"> · </span>
              <span className="text-muted-foreground">{extensionLabel}</span>
            </h2>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {showCopyAction ? (
              <div className="relative inline-flex overflow-hidden rounded-lg border border-border bg-background shadow-sm">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!canCopy}
                  className="h-7 rounded-none px-2.5 text-xs font-normal"
                  onClick={copyContent}
                >
                  {copied ? <Check className="h-3 w-3" /> : null}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <span className="w-px self-stretch bg-border" aria-hidden />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-6 rounded-none"
                      aria-label="More options"
                    >
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadFile}>
                      <Download className="h-4 w-4" />
                      <span>Download as {extensionLabel}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : showDownloadAction ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Download"
                    onClick={downloadFile}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download</TooltipContent>
              </Tooltip>
            ) : null}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="hidden h-8 w-8 md:inline-flex"
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  onClick={toggleExpanded}
                >
                  {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isExpanded ? 'Collapse' : 'Expand'}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Close"
                  onClick={closeFile}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Close</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div
          className={cn(
            'min-h-0 flex-1 bg-background',
            isBinaryDocument ? 'overflow-hidden' : 'overflow-auto'
          )}
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">Unable to load this file.</p>
              <Button type="button" variant="outline" size="sm" onClick={downloadFile}>
                Download
              </Button>
            </div>
          ) : tooLarge ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                This file is too large to preview. Download it to view the contents.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={downloadFile}>
                Download
              </Button>
            </div>
          ) : viewMode === 'code' ? (
            renderCodeView()
          ) : (
            renderPreview()
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
};
